import {Component, ElementRef, OnInit, QueryList, ViewChildren} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {of, Subject} from 'rxjs';
import {debounceTime, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {AuthenticationService} from 'src/app/core/authentication.service';
import {NotificationService} from '../../notification.service';
import {PermissionService} from '../../../core/permission.service';
import {groupBy} from 'lodash-es';
import {IGroupPermissions, IPermission} from 'src/app/core/permission';
import {RolesService} from "../../../users/roles/roles.service";
import {marker} from "@biesbjerg/ngx-translate-extract-marker";


@Component({
    selector: 'app-permissions-tool',
    templateUrl: './permissions-tool.component.html',
    styleUrls: ['./permissions-tool.component.scss'],
    standalone: false
})
export class PermissionsToolComponent implements OnInit {

  private destroyed$: Subject<void> = new Subject<void>();
  filteredPermissionsArray = [];
  permissionsForm: FormGroup;
  searchFormGroup: FormGroup;
  modulesList: string[] = [];
  permissionsArray = [];
  roles = [];
  groupedPermissionsArray = [];
  filteredGroupedPermissionsArray = [];
  searchTerm = '';

  @ViewChildren('amodule', { read: ElementRef }) renderedUsers: QueryList<ElementRef>;
  indexToScrollTo = 0;

  constructor(
    public authenticationService: AuthenticationService,
    private fb: FormBuilder,
    private notificationService: NotificationService,
    public permissionsService: PermissionService,
    public translateService: TranslateService,
    private rolesService: RolesService,
  ) {
  }

  ngOnInit() {
    this.permissionsForm = this.fb.group({
      role: [''],
      module: [''],
      permissions: this.fb.array([]),
    });
    this.getRoles();
    this.getPermissions();

    this.authenticationService.hookListActionOnOrgChange(() => {
      this.getRoles();
      this.getPermissions();
    }, this.destroyed$);

    this.searchFormGroup = this.fb.group({
      searchKey: ['']
    });

    this.searchFormGroup
      .get('searchKey')
      .valueChanges
      .pipe(
        debounceTime(300),
        map(value => value.toLowerCase()),
        tap((value) => {
          this.searchTerm = value;
        }),
        switchMap(value => of([...this.groupedPermissionsArray
          .filter(group => group.key.toLowerCase().includes(value) ||
                  group.items.filter(el => el.permissionRight.toLowerCase().includes(value)).length > 0 )
        ])
        ),
      )
      .subscribe(response => {
        this.filteredGroupedPermissionsArray = [...response
        .map(group => ({'key': group.key, 'items': this.filterPermissionsByRole(group.items)} ))
        .map(group => ({ 'key': group.key, 'items': this.filterPermissionsBySearchTerm(group)} ))
        ];
      });
  }

  displayPermissionName(permission?: IPermission) {
    return permission;
  }

  onGroupSelected(group:IGroupPermissions) {
    this.setSelectedModule(group.key);
    this.onModuleChanged();
  }

  onPermissionSelected(permission: IPermission) {
    this.setSelectedModule(permission.module);
    this.onModuleChanged();
  }

  get permissionsFormArray() {
    return (<FormArray>this.permissionsForm.get('permissions'));
  }

  updatePermissions() {
    const permissions = this.buildPermissionsData();
    this.permissionsService.updatePermissionsBulk(permissions)
    .pipe(takeUntil(this.destroyed$))
    .subscribe(
      () => {
        this.notificationService.success(this.translateService.stream(marker('Permissions updated successfully!')));
        this.getPermissions();
      });
  }

  addCustomLabel(label: any) {
    let permissions = this.permissionsForm.get('permissions') as FormArray;
    permissions.push(new FormControl(label.name));
  }

  selectAllPermissions(enable: boolean) {
    const values = this.filteredPermissionsArray.map(() => enable);
    this.permissionsFormArray.setValue(values);
  }

  onRoleChanged() {
    this.filteredGroupedPermissionsArray = [...this.groupedPermissionsArray
      .map(group => ({'key': group.key, 'items': this.filterPermissionsByRole(group.items)} ))
      ];
    this.onModuleChanged();
  }

  onModuleChanged() {
    const selectedRoleId = this.permissionsForm.get('role').value;
    const module = this.permissionsForm.get('module').value;
    const permissions = [...this.permissionsArray.filter(item => `${item.businessRoleId}` === `${selectedRoleId}` && item.module === module)];
    this.seedPermissionsArray(permissions);
  }

  get roleSelected(): boolean {
    return this.permissionsForm.get('role').value !== '';
  }

  get moduleSelected(): boolean {
    return this.permissionsForm.get('module').value !== '';
  }

  get roleAndModuleSelected(): boolean{
    return this.roleSelected && this.moduleSelected;
  }

  private scrollToSelectedModule(module: string) {
    this.indexToScrollTo = this.modulesList.indexOf(module);
    const userToScrollOn = this.renderedUsers.toArray();
    userToScrollOn[this.indexToScrollTo].nativeElement.scrollIntoView({
      behavior: 'smooth'
    });
  }

  private buildPermissionsData() {
   const permissions = [];
    this.permissionsFormArray.controls.forEach((item, index) => {
      permissions.push({
        permissionId: this.filteredPermissionsArray[index].id,
        roleId: +this.permissionsForm.value.role,
        status: item?.value !== false,
      });
    });
    return permissions;
  }

  private setSelectedModule(module: string) {
    const moduleId = this.modulesList.indexOf(module);
    this.permissionsForm.patchValue({
      module: this.modulesList[moduleId]
    });
    this.scrollToSelectedModule(module);
  }

  private getPermissions() {

    this.permissionsService.getAllPermissions()
      .subscribe(permissions => {
        this.permissionsArray = permissions;

        const groupedPermissionsArray = [];
        const groups = groupBy(permissions, function (permission) {
          return permission['module'];
        });
        Object.keys(groups)
          .map(function (key) {
            groupedPermissionsArray.push({ 'key': key, 'items': groups[key] })
            return groupedPermissionsArray;
          });
        this.groupedPermissionsArray = [...groupedPermissionsArray];
        this.filteredGroupedPermissionsArray = [...this.groupedPermissionsArray];
        this.getModules();
      });
  }

  private getModules() {
    this.permissionsArray.forEach(permission => {
      if (this.modulesList.indexOf(permission.module) === -1) {
        this.modulesList.push(permission.module);
      }
    });
    this.modulesList = this.modulesList.sort((a, b) => (a > b) ? 1 : -1);
  }

  private seedPermissionsArray(permissions) {

    this.filteredPermissionsArray = permissions;
    this.permissionsFormArray.clear();
    const values = this.filteredPermissionsArray.map(item => item.isActive);
    permissions.forEach((item) => {
      this.addCustomLabel(item);
    });
    this.permissionsFormArray.setValue(values);
  }

  private getRoles(): void {
    this.roles = this.rolesService.getRolesAsIdName();
  }

  private filterPermissionsByRole(permissions: IPermission[]) {
    if (this.permissionsForm.get('role').value !== undefined && this.permissionsForm.get('role').value !== '') {
      return [...permissions.filter(perm => perm.businessRoleId === this.permissionsForm.get('role').value)];
    }
    return [...permissions];
  }

  private filterPermissionsBySearchTerm(group: IGroupPermissions) {
    const permissions = [...group.items];
    const new_permissions = [...permissions.filter(perm => perm.permissionRight.toLowerCase().includes(this.searchTerm))];
    if (new_permissions.length === 0 && group.key.toLowerCase() === this.searchTerm) {
      return permissions;
    }
    return new_permissions;
  }
}
